/**
 * Formatage central des dates — contrat back « Dates & fuseaux ».
 *
 * Le back envoie exactement deux formes, et la forme dit quoi faire :
 *   - INSTANT : ISO avec Z ("2026-07-15T19:51:38.193Z") — créations, modifications,
 *     signatures, envois, connexions, dates du flux… → convertir au fuseau du
 *     navigateur pour l'affichage → `formatInstant` / `formatInstantDay`.
 *   - DATE CALENDAIRE : ISO sans Z, heure à minuit ("1985-07-14T00:00:00") —
 *     naissances, dates d'effet, échéances, périodes… → afficher le jour TEL QUEL,
 *     ne JAMAIS convertir (une date de naissance est la même partout sur Terre)
 *     → `formatDay`.
 *
 * Règle d'or : Z = on convertit, pas de Z = on ne touche pas.
 *
 * Envoi vers le back : une date calendaire (valeur d'un picker « jour ») s'envoie
 * en STRING "yyyy-MM-dd" de bout en bout — jamais `new Date(value).toISOString()`,
 * qui décale d'un jour selon le fuseau → `toDayPayload`.
 */

const INVALID = '—';

/** dd/mm/yyyy à partir des 10 premiers caractères ("yyyy-mm-dd…"), zéro conversion. */
const splitDay = (iso: string): string | null => {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
};

/**
 * INSTANT (ISO avec Z) → date + heure dans le fuseau du navigateur.
 * Ex. "2026-07-15T19:51:38Z" → "15/07/2026 21:51" à Paris, "15/07/2026 15:51" en Guadeloupe.
 */
export const formatInstant = (iso?: string | Date | null): string => {
  if (!iso) return INVALID;
  const d = iso instanceof Date ? iso : new Date(iso);
  if (isNaN(d.getTime())) return INVALID;
  return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
};

/** INSTANT (ISO avec Z) → jour seul dans le fuseau du navigateur (ex. "15/07/2026"). */
export const formatInstantDay = (iso?: string | Date | null): string => {
  if (!iso) return INVALID;
  const d = iso instanceof Date ? iso : new Date(iso);
  if (isNaN(d.getTime())) return INVALID;
  return d.toLocaleDateString('fr-FR');
};

/**
 * DATE CALENDAIRE (ISO sans Z) → "jj/mm/aaaa" par découpe de string, AUCUNE
 * conversion de fuseau. Accepte "1985-07-14", "1985-07-14T00:00:00", ou un Date
 * déjà désérialisé par les clients générés (lu en UTC pour retrouver le jour voulu).
 */
export const formatDay = (iso?: string | Date | null): string => {
  if (!iso) return INVALID;
  if (iso instanceof Date) {
    // Date issue d'un client généré : new Date("1985-07-14T00:00:00") (ISO naïf)
    // est parsé en minuit LOCAL — les getters locaux redonnent le jour d'origine.
    // Surtout pas .toISOString() (repasse en UTC → jour précédent selon le fuseau).
    if (isNaN(iso.getTime())) return INVALID;
    const dd = String(iso.getDate()).padStart(2, '0');
    const mm = String(iso.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${iso.getFullYear()}`;
  }
  return splitDay(iso) ?? INVALID;
};

/**
 * DATE CALENDAIRE → valeur d'un <input type="date"> ("yyyy-MM-dd"), sans conversion.
 * Accepte la string back ("1985-07-14T00:00:00") ou un Date désérialisé par un client
 * généré (ISO naïf = minuit local → getters locaux, jamais toISOString()).
 */
export const toDayInput = (iso?: string | Date | null): string => {
  if (!iso) return '';
  if (iso instanceof Date) {
    if (isNaN(iso.getTime())) return '';
    const dd = String(iso.getDate()).padStart(2, '0');
    const mm = String(iso.getMonth() + 1).padStart(2, '0');
    return `${iso.getFullYear()}-${mm}-${dd}`;
  }
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
};

/**
 * Valeur d'un picker « jour » → payload back : "yyyy-MM-dd" sec, sans heure ni
 * offset. Reste en string de bout en bout — jamais d'objet Date sérialisé.
 */
export const toDayPayload = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : undefined;
};
